import type { AccrualPosition, MarketParams } from '@morpho-org/blue-sdk'

import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { useWeb3Context } from '@/contexts/Web3Context'
import { useLatestBlock } from '@/hooks/useLatestBlock'

/**
 * Market-actions-v2 sister of `useVaultV2`: binds a `MorphoBlue` entity to the
 * connected client (`client.morpho.blue(marketParams, chainId)`) and polls the
 * user's accrual position.
 *
 * The returned `positionData` (`AccrualPosition`, carrying `.market` with the
 * oracle `price`) is the single source the builder uses for morpho-sdk health
 * validation (borrow / withdraw / combos) and the recap reads for the current
 * position + LTV. The read is pinned to the shared {@link useLatestBlock}
 * snapshot so the position/market and the public-allocator reallocation fan-out
 * (`useMarketReallocations`) are evaluated at the **same block** — otherwise the
 * max-borrow mixes a `latest` market with a pinned-block `maxReallocatable`. The
 * block is polled every 10s and the query re-keys on it, so a new block refreshes
 * the position together with the reallocation data.
 *
 * @param marketParams - the target market's params (from
 *   `apiState.tryGetMarket(marketId)?.params`); `undefined` while loading.
 */
export function useMarketBlue(marketParams: MarketParams | undefined) {
  const { viemClient, targetChainId, account } = useWeb3Context()

  const market = useMemo(
    () => (marketParams ? viemClient.morpho.blue(marketParams, targetChainId) : undefined),
    [viemClient, marketParams, targetChainId]
  )

  // Shared recent-block snapshot (deduped across every market-actions-v2 read). The position is
  // read AT this block so it lines up with `useMarketReallocations`' block-pinned fan-out.
  const { data: block, error: blockError } = useLatestBlock(targetChainId, {
    enabled: !!market && !!account.address
  })
  const blockNumber = block?.number ?? undefined

  const {
    data: snapshot,
    error: positionError,
    isLoading,
    refetch
  } = useQuery<{
    positionData: AccrualPosition
    blockTimestamp: bigint
  }>({
    // Recreate when the client changes (chain/account switch) so reads target the right chain;
    // re-key on `blockNumber` so the position refreshes in lockstep with the reallocation data.
    queryKey: [
      'market-blue-position',
      marketParams?.id,
      account.address,
      targetChainId,
      viemClient,
      market,
      blockNumber,
      block?.timestamp
    ],
    queryFn: async () => {
      const positionData = await market!.getPositionData(account.address!, { blockNumber })
      return {
        // `getPositionData` wraps the raw storage snapshot. Explicitly accrue it to that snapshot's
        // timestamp before any MAX math or SDK validation consumes `supplyAssets` / `borrowAssets`.
        positionData: positionData.accrueInterest(block!.timestamp),
        blockTimestamp: block!.timestamp
      }
    },
    // Gate on the resolved block so the position is only ever read at the shared snapshot — never a
    // stray `latest` read that would desync it from the reallocation data.
    enabled: !!market && !!account.address && blockNumber != null,
    // Keep the previous position ONLY across snapshot-block refetches for the SAME
    // market/account/chain, so the recap doesn't flash a skeleton every ~10s. Drop it on a
    // market/account/chain switch: plain `keepPreviousData` would otherwise surface the PREVIOUS
    // account's `AccrualPosition` (with `isLoading` false) under the new key, and the builder /
    // panels would compute max amounts, health, and even build a tx against that stale, wrong-account
    // state until the fresh read lands. Returning `undefined` makes the query report loading so
    // consumers gate correctly.
    // queryKey layout (keep this index map in sync): [0]=label, [1]=marketParams.id,
    // [2]=account.address, [3]=targetChainId, [4]=viemClient, [5]=market, [6]=blockNumber,
    // [7]=blockTimestamp.
    placeholderData: (previousData, previousQuery) => {
      if (previousData == null || !previousQuery) return undefined
      const [, prevMarketId, prevAccount, prevChainId] = previousQuery.queryKey
      return prevMarketId === marketParams?.id &&
        prevAccount === account.address &&
        prevChainId === targetChainId
        ? previousData
        : undefined
    }
  })

  const positionData = snapshot?.positionData

  return {
    market,
    positionData,
    /** Timestamp of the snapshot block the position was read and accrued at. */
    snapshotTimestamp: snapshot?.blockTimestamp,
    // Surface the block-resolution wait as loading too, so consumers don't see a false "loaded" gap
    // before the gated query starts.
    isLoading:
      account.isConnected && !blockError && !positionError && (isLoading || blockNumber == null),
    /** Latest-block or position-read failure for the existing service-unavailable paths. */
    error: blockError ?? positionError ?? null,
    refetchPosition: refetch
  }
}
